/**
 * Report Builder - Place Title JavaScript
 *
 * An editable H1–H4 heading on the report canvas. A simpler alternative to
 * adding a heading in a text block.
 */

(function () {
  "use strict";

  const DEFAULT_TITLE = "iIMPROVE PIVC Care Benchmarking";
  const LEVELS = ["h1", "h2", "h3", "h4"];
  const DEFAULT_LEVEL = "h2";

  let titleCounter = 0;
  let isAddingTitle = false;

  /**
   * Add a title directly to the canvas (no configuration step)
   */
  function addTitle() {
    // Prevent duplicate calls
    if (isAddingTitle) {
      return;
    }

    isAddingTitle = true;

    const canvasCharts = window.ReportBuilderUtils?.getCanvasContainer();
    if (!canvasCharts) {
      isAddingTitle = false;
      return;
    }

    if (window.ReportBuilderUtils?.toggleEmptyMessage) {
      window.ReportBuilderUtils.toggleEmptyMessage(false);
    }

    renderTitleInCanvas({
      id: `report_title_${++titleCounter}_${Date.now()}`,
      text: DEFAULT_TITLE,
      level: DEFAULT_LEVEL,
    });

    // Reset flag after a short delay
    setTimeout(function () {
      isAddingTitle = false;
    }, 100);
  }

  /**
   * Render title in canvas
   */
  function renderTitleInCanvas(titleConfig) {
    const canvasCharts = window.ReportBuilderUtils?.getCanvasContainer();
    if (!canvasCharts) {
      return;
    }

    const titleContainer = document.createElement("div");
    titleContainer.className = "canvas-title-item";
    titleContainer.id = `title_container_${titleConfig.id}`;
    titleContainer.setAttribute("data-title-id", titleConfig.id);
    // Read by the PDF export
    titleContainer.setAttribute("data-title-level", titleConfig.level);

    const levelOptions = LEVELS.map(function (level) {
      const selected = level === titleConfig.level ? " selected" : "";
      return `<option value="${level}"${selected}>${level.toUpperCase()}</option>`;
    }).join("");

    const titleHeader = document.createElement("div");
    titleHeader.className = "title-item-header";
    titleHeader.innerHTML = window.ReportBuilderUtils.generateItemHeaderHtml({
      itemId: titleConfig.id,
      displayTitle: "Title",
      idAttribute: "data-title-id",
      itemType: "title",
      removeButtonClass: "btn-remove-title",
      removeButtonTitle: "Remove title",
      hideEditTitle: true, // The heading itself is edited in place
      actionsHtml: `<select class="title-level-select" title="Heading size" aria-label="Heading size">${levelOptions}</select>`,
    });

    const titleContent = document.createElement("div");
    titleContent.className = "title-item-content";
    titleContent.appendChild(createHeading(titleConfig.level, titleConfig.text));

    // Pencil icon to show the title is editable (same as clicking the heading)
    const editBtn = document.createElement("button");
    editBtn.type = "button";
    editBtn.className = "btn-edit-title-heading";
    editBtn.title = "Edit title";
    editBtn.setAttribute("aria-label", "Edit title");
    editBtn.innerHTML = `
      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
      </svg>`;
    editBtn.addEventListener("click", function () {
      // Looked up on click, as changing the size replaces the heading
      const heading = titleContent.querySelector(".title-item-heading");
      if (heading) heading.click();
    });
    titleContent.appendChild(editBtn);

    titleContainer.appendChild(titleHeader);
    titleContainer.appendChild(titleContent);
    canvasCharts.appendChild(titleContainer);

    titleContainer.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
    });

    // Changing the size swaps the heading element, keeping its text
    titleHeader
      .querySelector(".title-level-select")
      .addEventListener("change", function () {
        const level = LEVELS.indexOf(this.value) !== -1 ? this.value : DEFAULT_LEVEL;
        const current = titleContent.querySelector(".title-item-heading");
        titleContent.replaceChild(
          createHeading(level, current ? current.textContent : ""),
          current
        );
        titleContainer.setAttribute("data-title-level", level);
      });

    if (window.ReportBuilderUtils?.setupMoveButtons) {
      window.ReportBuilderUtils.setupMoveButtons(
        titleContainer,
        titleConfig.id,
        "data-title-id",
        function () {}
      );
    }

    const removeBtn = titleContainer.querySelector(".btn-remove-title");
    if (removeBtn) {
      removeBtn.addEventListener("click", function () {
        if (window.ReportBuilderUtils?.showConfirmationModal) {
          window.ReportBuilderUtils.showConfirmationModal({
            title: "Remove Title",
            message: "Are you sure you want to remove this title?",
            confirmText: "Remove",
            cancelText: "Cancel",
            onConfirm: function () {
              removeTitle(titleConfig.id);
            },
          });
        } else if (confirm("Are you sure you want to remove this title?")) {
          removeTitle(titleConfig.id);
        }
      });
    }
  }

  /**
   * Create the editable heading. When empty it shows a placeholder and is
   * left out of the PDF.
   */
  function createHeading(level, text) {
    const heading = document.createElement(level);
    heading.className = "title-item-heading";
    heading.setAttribute("data-placeholder", "Add a title");
    heading.title = "Click to edit";
    heading.textContent = text;

    if (window.ReportBuilderUtils?.makeReportHeaderEditable) {
      window.ReportBuilderUtils.makeReportHeaderEditable(heading, null, "");
    }

    return heading;
  }

  /**
   * Remove title from canvas
   */
  function removeTitle(titleId) {
    const titleContainer = document.getElementById(`title_container_${titleId}`);
    if (titleContainer) {
      titleContainer.remove();
    }

    // Show empty message if no items left
    const canvasCharts = window.ReportBuilderUtils?.getCanvasContainer();
    if (canvasCharts && canvasCharts.children.length === 0) {
      if (window.ReportBuilderUtils?.toggleEmptyMessage) {
        window.ReportBuilderUtils.toggleEmptyMessage(true);
      }
    }
  }

  // Export functions for external use
  window.ReportBuilderTitle = {
    addTitle: addTitle,
    removeTitle: removeTitle,
  };
})();
